import { RPGTypes } from '@ps/poker-games-tile-mappers';
import { isUndefined } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import { FeatureFlag, ResolverTag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import useResolver from '@swf/core/hooks/useResolver';
import { select } from '@swf/core/store';
import Cookie from '@swf/core/types/Cookies';
import { useGetCurrentDate } from '@swf/poker';

export enum EncodedWebEndpointType {
  TABLE_STATIC_DATA = 'HOME_RING_TABLE',
  TOURNAMENT_STATIC_DATA = 'HOME_TOURNAMENT_STRUCTURE'
}

export interface Props {
  type: string;
  serverAddress: string;
  serverObjectName: string;
}

export const getType = (type: Props['type']) =>
  type === RPGTypes.CASH_GAME || type === RPGTypes.SAG
    ? EncodedWebEndpointType.TABLE_STATIC_DATA
    : EncodedWebEndpointType.TOURNAMENT_STATIC_DATA;

const cookies = new Cookies();

const getRamHash = (deepLinkConfig: object) => {
  const dictionary = {
    sbtk: 'ram_token',
    sbsg: 'ram_signature',
    wbid: 'ram_u',
    xiid: 'ram_devIx'
  };
  return Object.keys(dictionary)
    .map((key) => `${dictionary[key]}=${encodeURIComponent(deepLinkConfig[key])}`)
    .join('&');
};

const useWpGameUrl = ({ type, serverAddress, serverObjectName }: Props) => {
  const [url, setUrl] = useState<string>('');
  const newTab = useFeature(FeatureFlag.HomeGamesNewTab);
  const resolver = useResolver(ResolverTag.WebPoker);
  const request = useRequest();
  const brand = useSelector(select.site.getBrand);
  const site = useSelector(select.site.getId);
  const { timeZone } = useGetCurrentDate();
  const { token, signature, userWebId } = useSelector(select.session.getLegacySession);
  const country = useSelector(select.account.getCountry);

  useEffect(() => {
    if (!resolver || isUndefined(newTab)) {
      return;
    }
    request({
      service: 'homeGames.encodedWebEndpointRequest',
      body: { serverAddress, serverObjectName, type: getType(type) }
    }).subscribe(({ body: { encodedWebEndpoint } }) => {
      const deepLinkConfig = {
        endpoint: encodedWebEndpoint,
        gametype: type.toLowerCase(),
        sbtk: token,
        sbsg: signature,
        wbid: userWebId,
        site,
        brand,
        xiid: cookies.get(Cookie.INSTALL_ID),
        country,
        origin: window.location.origin,
        timezone: timeZone,
        newtab: newTab
      };

      let webPokerUrl = `${resolver}/?standalonetable=true&debug=true&args=${window.btoa(
        JSON.stringify(deepLinkConfig)
      )}`;

      if (newTab) {
        // In a future ticket WebPoker could extract RamHash from deeplink args instead of putting them in the url twice
        webPokerUrl += `&${getRamHash(deepLinkConfig)}`;
      }

      setUrl(webPokerUrl);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver, newTab]);

  return { url };
};

export default useWpGameUrl;
